import React, { useState, useEffect } from "react";
import { useField } from "react-final-form";
import clsx from "clsx";

interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  field: string;
  label?: string;
  rows?: number;
  labelTextColor?: string; // e.g. text-gray-900
}

export function TextAreaBase({
  id,
  field,
  label,
  placeholder,
  required,
  rows = 4,
  onChange,
  labelTextColor = "text-gray-900",
  value,
  ...others
}: TextAreaProps & { value: string }) {
  const textareaClasses = clsx(
    `block w-full px-4 rounded-md py-1.5 sm:leading-6 border-0
    shadow-sm ring-1 ring-inset ring-gray-300
    focus:ring-2 focus:ring-inset focus:ring-primary-600 focus:outline-none
    disabled:placeholder:text-gray-300 disabled:ring-gray-200 disabled:text-gray-400 disabled:bg-neutral-0
    placeholder:text-gray-400 text-gray-900`,
  );

  return (
    <div>
      {label && (
        <label
          htmlFor={field}
          className={clsx("block text-sm font-medium leading-6", labelTextColor)}
        >
          {label}
        </label>
      )}
      <textarea
        id={id}
        name={field}
        rows={rows}
        className={textareaClasses}
        placeholder={placeholder}
        onChange={onChange}
        required={required}
        value={value}
        {...others}
      />
    </div>
  );
}

const TextArea = ({ field, onChange, ...others }: TextAreaProps) => {
  const f = useField(field);
  const initialValue = f.input.value || f.input.defaultValue;
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    const formValue = f.input.value || f.input.defaultValue;
    if (formValue !== value) {
      setValue(formValue);
    }
  }, [f.input.value, f.input.defaultValue]);

  const onChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    f.input.onChange(e);
    if (onChange) {
      onChange(e);
    }
  };

  return (
    <TextAreaBase {...f.input} onChange={onChangeHandler} value={value} field={field} {...others} />
  );
};

export default TextArea;
